// apps/web/src/people/LeavesView.tsx
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { peopleClient, useCore } from "@praesto/core";
import type { Leave } from "@praesto/shared-types";
import {
  Tabs,
  Badge,
  Modal,
  Button,
  Textarea,
  EmptyState,
  Section,
  SkeletonTableRows,
  PageFull,
} from "@praesto/ui-kit";

const typeColor: Record<Leave["type"], string> = {
  vacation: "bg-success-soft text-success",
  permit: "bg-info-soft text-info",
  sick: "bg-danger-soft text-danger",
  // Neutral: a statutory leave is not an illness and must not be coloured as one.
  leave: "bg-info-soft text-info",
};
const typeLabel: Record<Leave["type"], string> = {
  vacation: "Ferie",
  permit: "Permesso",
  sick: "Malattia",
  // Says the person is away, never why (see LeaveType in shared-types).
  leave: "Congedo",
};

function monthDays(year: number, month0: number): string[] {
  const last = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const out: string[] = [];
  for (let d = 1; d <= last; d++) {
    out.push(`${year}-${String(month0 + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }
  return out;
}

export function LeavesView() {
  const core = useCore();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [tab, setTab] = useState<"calendar" | "queue">("calendar");
  const today = new Date();
  const [year, setYear] = useState(today.getUTCFullYear());
  const [month, setMonth] = useState(today.getUTCMonth()); // 0-based

  const monthStart = useMemo(
    () => `${year}-${String(month + 1).padStart(2, "0")}-01`,
    [year, month],
  );
  const monthEnd = useMemo(() => {
    const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return `${year}-${String(month + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
  }, [year, month]);

  const [monthLeaves, setMonthLeaves] = useState<Leave[]>([]);
  const [pending, setPending] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<{ id: string } | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  async function reload() {
    setError(null);
    const [m, p] = await Promise.all([
      peopleClient.listLeaves({ status: "approved", from: monthStart, to: monthEnd }),
      peopleClient.listLeaves({ status: "pending" }),
    ]);
    setMonthLeaves(m);
    setPending(p);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload()
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthStart, monthEnd]);

  async function approve(id: string) {
    await peopleClient.approveLeave(id);
    await reload();
  }
  async function submitReject() {
    if (!rejectModal) return;
    await peopleClient.rejectLeave(rejectModal.id, rejectReason);
    setRejectModal(null);
    setRejectReason("");
    await reload();
  }

  const days = useMemo(() => monthDays(year, month), [year, month]);
  const leavesByDate: Map<string, Leave[]> = useMemo(() => {
    const m = new Map<string, Leave[]>();
    for (const d of days) {
      const arr = monthLeaves.filter((l) => d >= l.startDate && d <= l.endDate);
      if (arr.length > 0) m.set(d, arr);
    }
    return m;
  }, [days, monthLeaves]);

  function prevMonth() {
    if (month === 0) {
      setYear(year - 1);
      setMonth(11);
    } else setMonth(month - 1);
  }
  function nextMonth() {
    if (month === 11) {
      setYear(year + 1);
      setMonth(0);
    } else setMonth(month + 1);
  }
  function thisMonth() {
    setYear(today.getUTCFullYear());
    setMonth(today.getUTCMonth());
  }

  return (
    <PageFull>
      <Section width="readable">
        <Tabs
          variant="underline"
          className="mb-6"
          active={tab}
          onChange={(id) => setTab(id as "calendar" | "queue")}
          items={[
            { id: "calendar", label: "Calendario" },
            {
              id: "queue",
              label: "In attesa",
              // v18 Tabs: numeric counters ride the `count` pill, not a Badge.
              ...(pending.length > 0 ? { count: pending.length } : {}),
            },
          ]}
        />

        {error && <p className="text-sm text-danger mb-3">{error}</p>}
        {loading && <SkeletonTableRows rows={5} cols={7} firstSticky={false} />}

        {tab === "calendar" && (
          <div>
            <header className="flex items-center justify-between mb-4">
              <div className="text-sm font-medium text-fg-default capitalize">
                {new Date(Date.UTC(year, month, 1)).toLocaleString("it-IT", {
                  month: "long",
                  year: "numeric",
                })}
              </div>
              <div className="flex gap-2">
                <Button
                  size="md"
                  variant="ghost"
                  className="tap-target px-3 sm:h-9"
                  aria-label={core.i18n.t("people.nav.month_prev")}
                  onClick={prevMonth}
                >
                  <ChevronLeft size={16} aria-hidden />
                </Button>
                <Button size="md" variant="secondary" className="sm:h-9" onClick={thisMonth}>
                  Oggi
                </Button>
                <Button
                  size="md"
                  variant="ghost"
                  className="tap-target px-3 sm:h-9"
                  aria-label={core.i18n.t("people.nav.month_next")}
                  onClick={nextMonth}
                >
                  <ChevronRight size={16} aria-hidden />
                </Button>
              </div>
            </header>
            <div className="grid grid-cols-7 gap-1 text-mono-xs">
              {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => (
                <div key={i} className="text-center p-1 text-fg-muted uppercase">
                  {d}
                </div>
              ))}
              {/* pad start — align first day of month to correct weekday column */}
              {(() => {
                const first = new Date(Date.UTC(year, month, 1)).getUTCDay();
                const pad = (first + 6) % 7;
                return Array.from({ length: pad }, (_, i) => <div key={`p${i}`} className="p-1" />);
              })()}
              {days.map((d) => {
                const arr = leavesByDate.get(d) ?? [];
                return (
                  <div key={d} className="border border-border-default rounded-md p-1 min-h-[60px]">
                    <div className="text-mono-xs font-semibold text-fg-default mb-0.5">
                      {d.slice(8)}
                    </div>
                    <div className="space-y-0.5">
                      {arr.slice(0, 3).map((l) => (
                        <Link
                          key={l.id}
                          to={`/${tenantSlug}/people/${l.userId}/leaves` as never}
                          className={`block px-1 py-1 rounded text-mono-xs truncate ${typeColor[l.type]}`}
                          title={`${l.userId} · ${typeLabel[l.type]}`}
                        >
                          {typeLabel[l.type]}
                        </Link>
                      ))}
                      {arr.length > 3 && (
                        <div className="text-mono-xs text-fg-muted">+{arr.length - 3}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "queue" && (
          <div>
            {pending.length === 0 && !loading && (
              <EmptyState
                title="Tutto a posto"
                description="Nessuna richiesta in attesa."
                size="compact"
              />
            )}
            <ul className="space-y-2">
              {pending.map((l) => (
                <li
                  key={l.id}
                  className="border border-border-default rounded-md px-3 py-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-elevated"
                >
                  <div className="flex items-center gap-3 flex-wrap min-w-0">
                    <Link
                      to={`/${tenantSlug}/people/${l.userId}/leaves` as never}
                      className="text-sm font-mono hover:underline text-fg-default truncate max-w-[55vw]"
                    >
                      {l.userId}
                    </Link>
                    <Badge
                      tone={
                        l.type === "vacation" ? "success" : l.type === "permit" ? "info" : "danger"
                      }
                      variant="soft"
                      size="xs"
                    >
                      {typeLabel[l.type]}
                    </Badge>
                    <span className="whitespace-nowrap text-mono-xs text-fg-muted">
                      {l.startDate} → {l.endDate} · {l.daysCount}gg
                    </span>
                    {l.reason && (
                      <span className="text-caption text-fg-muted italic">"{l.reason}"</span>
                    )}
                  </div>
                  <div className="flex gap-2 sm:flex-shrink-0">
                    <Button
                      size="md"
                      variant="primary"
                      className="flex-1 sm:flex-none"
                      onClick={() => void approve(l.id)}
                    >
                      Approva
                    </Button>
                    <Button
                      size="md"
                      variant="danger"
                      className="flex-1 sm:flex-none"
                      onClick={() => setRejectModal({ id: l.id })}
                    >
                      Rifiuta
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Modal
          open={!!rejectModal}
          onClose={() => {
            setRejectModal(null);
            setRejectReason("");
          }}
          title="Motivo del rifiuto"
          size="sm"
        >
          <Textarea
            rows={4}
            maxLength={500}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Spiega perché stai rifiutando..."
          />
          <div className="flex flex-col-reverse gap-2 mt-3 sm:flex-row sm:justify-end">
            <Button
              size="md"
              variant="ghost"
              className="w-full sm:w-auto"
              onClick={() => {
                setRejectModal(null);
                setRejectReason("");
              }}
            >
              Annulla
            </Button>
            <Button
              size="md"
              variant="danger"
              className="w-full sm:w-auto"
              disabled={rejectReason.trim().length === 0}
              onClick={() => void submitReject()}
            >
              Conferma rifiuto
            </Button>
          </div>
        </Modal>
      </Section>
    </PageFull>
  );
}
